import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Zoom,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import FormatSizeOutlinedIcon from "@mui/icons-material/FormatSizeOutlined";
import { useSnackbar } from "notistack";
import { tokens } from "../../theme";
import { getAuthHeaders } from "../../utils/authToken";
import ChatMessage from "./ChatMessage";

const EXAMPLE_PROMPTS = [
  "Show all courses",
  "Export info for students enrolled this semester",
  "List students currently taking classes",
];

const FONT_SIZE_PRESETS = {
  small:  { message: "12px", input: "12px", prompt: "11px", status: "11px", download: "11px", lineHeight: 1.5 },
  medium: { message: "14px", input: "13px", prompt: "12px", status: "12px", download: "12px", lineHeight: 1.5 },
  large:  { message: "16px", input: "15px", prompt: "14px", status: "14px", download: "13px", lineHeight: 1.6 },
  xlarge: { message: "18px", input: "17px", prompt: "16px", status: "16px", download: "15px", lineHeight: 1.7 },
};

const ChatPanel = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("chatViewMode") || "small"
  ); // "small" | "half" | "full"
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [statusText, setStatusText] = useState("Thinking...");
  const [streamingContent, setStreamingContent] = useState("");
  const [fontSize, setFontSize] = useState(
    localStorage.getItem("chatFontSize") || "medium"
  );
  const [fontAnchorEl, setFontAnchorEl] = useState(null);
  const fontPopoverOpen = Boolean(fontAnchorEl);
  const fontConfig = FONT_SIZE_PRESETS[fontSize];

  const VIEW_SIZES = {
    small: { width: 400, height: 550, bottom: 24, right: 24, borderRadius: "12px" },
    half: { width: "50vw", height: "100vh", bottom: 0, right: 0, borderRadius: 0 },
    full: { width: "100vw", height: "100vh", bottom: 0, right: 0, borderRadius: 0 },
  };

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Auto-scroll to bottom on new messages or streaming content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, streamingContent]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close panel on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        abortControllerRef.current?.abort();
        setIsOpen(false);
        setStreamingContent("");
        setIsLoading(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  // Abort streaming on unmount
  useEffect(() => {
    return () => { abortControllerRef.current?.abort(); };
  }, []);

  const sendMessage = useCallback(async (text) => {
    const content = text || input.trim();
    if (!content || isLoading) return;

    const userMessage = { role: "user", content };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    setStatusText("Thinking...");
    setStreamingContent("");

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const payload = {
        messages: newMessages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
      };

      const response = await fetch(
        `${process.env.REACT_APP_URL}/api/v1/chat-stream`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          window.location.replace("/signin");
          return;
        }
        if (response.status === 403) {
          enqueueSnackbar("You don't have permission to use the AI assistant.", {
            variant: "error",
          });
          setMessages(messages);
          setIsLoading(false);
          return;
        }
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || "Failed to get response. Please try again.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      let fileAttachment = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // Keep the last incomplete line in the buffer
        buffer = lines.pop();

        let eventType = null;
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ") && eventType) {
            try {
              const data = JSON.parse(line.slice(6));

              if (eventType === "status") {
                setStatusText(data.message || "Thinking...");
              } else if (eventType === "delta") {
                accumulated += data.content;
                setStreamingContent(accumulated);
              } else if (eventType === "done") {
                if (data.file) fileAttachment = data.file;
              } else if (eventType === "error") {
                throw new Error(data.error);
              }
            } catch (parseErr) {
              if (parseErr.message && !parseErr.message.includes("JSON")) {
                throw parseErr;
              }
            }
            eventType = null;
          }
        }
      }

      // Finalize: add the complete assistant message
      const assistantMessage = { role: "assistant", content: accumulated };
      if (fileAttachment) assistantMessage.file = fileAttachment;
      setMessages((prev) => [...prev, assistantMessage]);
      setStreamingContent("");
    } catch (error) {
      if (error.name === "AbortError") return;
      enqueueSnackbar(
        error.message || "Failed to get response. Please try again.",
        { variant: "error" }
      );
      setMessages(messages);
      setStreamingContent("");
    } finally {
      setIsLoading(false);
      setStatusText("Thinking...");
      abortControllerRef.current = null;
    }
  }, [input, isLoading, messages, enqueueSnackbar]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setStreamingContent("");
    setIsLoading(false);
    setStatusText("Thinking...");
  };

  return (
    <>
      {/* FAB Button */}
      <Zoom in={!isOpen}>
        <Fab
          onClick={() => setIsOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1300,
            bgcolor: colors.blueAccent[600],
            "&:hover": { bgcolor: colors.blueAccent[700] },
          }}
        >
          <SmartToyOutlinedIcon sx={{ color: "#fff" }} />
        </Fab>
      </Zoom>

      {/* Chat Panel */}
      <Zoom in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: VIEW_SIZES[viewMode].bottom,
            right: VIEW_SIZES[viewMode].right,
            width: VIEW_SIZES[viewMode].width,
            height: VIEW_SIZES[viewMode].height,
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            borderRadius: VIEW_SIZES[viewMode].borderRadius,
            overflow: "hidden",
            transition: "width 0.3s ease, height 0.3s ease, bottom 0.3s ease, right 0.3s ease, border-radius 0.3s ease",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: colors.blueAccent[700],
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{ color: "#fff", fontWeight: 600 }}
              >
                AONE AI Assistant
              </Typography>
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={(e) => setFontAnchorEl(e.currentTarget)}
                sx={{ color: "#fff" }}
              >
                <FormatSizeOutlinedIcon fontSize="small" />
              </IconButton>
              <Popover
                open={fontPopoverOpen}
                anchorEl={fontAnchorEl}
                onClose={() => setFontAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiPopover-paper": {
                    borderRadius: "8px",
                    bgcolor: colors.blueAccent[700],
                    p: 1,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ px: 1, color: "#fff", fontSize: "11px" }}
                >
                  Text Size
                </Typography>
                <ToggleButtonGroup
                  value={fontSize}
                  exclusive
                  onChange={(e, newSize) => {
                    if (newSize) {
                      setFontSize(newSize);
                      localStorage.setItem("chatFontSize", newSize);
                    }
                  }}
                  size="small"
                  sx={{ display: "flex", mt: 0.5 }}
                >
                  <ToggleButton value="small" sx={{ fontSize: "11px", textTransform: "none", px: 1.5, color: "#fff", borderColor: "rgba(255,255,255,0.3)", "&.Mui-selected": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" } }}>
                    Small
                  </ToggleButton>
                  <ToggleButton value="medium" sx={{ fontSize: "11px", textTransform: "none", px: 1.5, color: "#fff", borderColor: "rgba(255,255,255,0.3)", "&.Mui-selected": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" } }}>
                    Medium
                  </ToggleButton>
                  <ToggleButton value="large" sx={{ fontSize: "11px", textTransform: "none", px: 1.5, color: "#fff", borderColor: "rgba(255,255,255,0.3)", "&.Mui-selected": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" } }}>
                    Large
                  </ToggleButton>
                  <ToggleButton value="xlarge" sx={{ fontSize: "11px", textTransform: "none", px: 1.5, color: "#fff", borderColor: "rgba(255,255,255,0.3)", "&.Mui-selected": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" } }}>
                    X-Large
                  </ToggleButton>
                </ToggleButtonGroup>
              </Popover>
              <IconButton
                size="small"
                onClick={() =>
                  setViewMode((prev) => {
                    const next = prev === "small" ? "half" : prev === "half" ? "full" : "small";
                    localStorage.setItem("chatViewMode", next);
                    return next;
                  })
                }
                sx={{ color: "#fff" }}
              >
                {viewMode === "full" ? (
                  <CloseFullscreenOutlinedIcon fontSize="small" />
                ) : (
                  <OpenInFullOutlinedIcon fontSize="small" />
                )}
              </IconButton>
              <IconButton size="small" onClick={clearChat} sx={{ color: "#fff" }}>
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  abortControllerRef.current?.abort();
                  setIsOpen(false);
                  setStreamingContent("");
                  setIsLoading(false);
                }}
                sx={{ color: "#fff" }}
              >
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 1.5,
              bgcolor: colors.primary[600],
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <SmartToyOutlinedIcon
                  sx={{ fontSize: 40, color: colors.grey[500], opacity: 0.5 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: colors.grey[500], textAlign: "center" }}
                >
                  Ask me anything about AONE Universe
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                  {EXAMPLE_PROMPTS.map((prompt) => (
                    <Box
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        border: `1px solid ${colors.grey[700]}`,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: colors.grey[800],
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.grey[300], fontSize: fontConfig.prompt }}>
                        {prompt}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <ChatMessage key={idx} message={msg} colors={colors} fontConfig={fontConfig} />
                ))}
                {streamingContent && (
                  <ChatMessage
                    message={{ role: "assistant", content: streamingContent }}
                    colors={colors}
                    fontConfig={fontConfig}
                  />
                )}
                {isLoading && !streamingContent && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <CircularProgress size={16} sx={{ color: colors.blueAccent[600] }} />
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[500], fontSize: fontConfig.status }}
                    >
                      {statusText}
                    </Typography>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: colors.primary[600],
              borderTop: `1px solid ${colors.grey[800]}`,
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: fontConfig.input,
                },
              }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              sx={{
                bgcolor: colors.blueAccent[600],
                color: "#fff",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: colors.blueAccent[700] },
                "&.Mui-disabled": {
                  bgcolor: colors.grey[700],
                  color: colors.grey[500],
                },
              }}
            >
              <SendOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
};

export default ChatPanel;
